import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";

// ─────────────────────────────────────────────────────────────────────────────
// Cuentas pendientes ("cuentas del cliente")
// ─────────────────────────────────────────────────────────────────────────────
//
// Una Cuenta es una sesión persistente con nombre que acumula pedidos a lo
// largo de varias visitas/mesas hasta que se decide cobrar. No hay tabla de
// saldos: el "saldo" es el sumatorio de los pedidos no pagados de la sesión
// asociada a la cuenta.
//
// Operación clave — "Añadir a cuenta":
//   - Coge la sesión activa de una mesa, reasigna sus Orders a la sesión de
//     la cuenta (creándola si no existía) y libera la mesa.
//   - El cobro se hace sobre la sesión de la cuenta cuando el cliente
//     decide pagar — usando el flujo `pedidos/SendPaymentAndPayFast` o
//     `pedidos/pagar/all` igual que con cualquier mesa.
//
// CONTRATO BACKEND ESPERADO:
//
//   GET    /cuentas
//     → 200 { cuentas: [{ id, name, apellidos?, email?, telefono?,
//                          idCustomer?, idSession?, total, ordersCount,
//                          createdAt, updatedAt, status }] }
//     `total` ya viene calculado por el backend (sum de pedidos no pagados).
//     `status` ∈ { 'open', 'paid', 'archived' }.
//
//   POST   /cuentas       body: { name, apellidos?, email?, telefono?, idCustomer? }
//     → 201 { cuenta: {...} }
//
//   PUT    /cuentas/{id}  body: { name, apellidos?, email?, telefono?, idCustomer? }
//     → 200 { cuenta: {...} }
//
//   GET    /cuentas/{id}/orders
//     → 200 { orders: [...] } (mismo shape que `state.order.orders`)
//
//   POST   /cuentas/{id}/anyadir/{sessionId}
//     → 200 { cuenta: {...} }   // cuenta actualizada con el total nuevo
//     Reasigna los Orders de `sessionId` a la sesión de la cuenta. Si la
//     cuenta no tenía sesión, la sesión de la mesa pasa a ser la de la
//     cuenta (`session.idCuenta = id, idMesa = null`). Marca la sesión
//     original de la mesa como cerrada/transferida.
//
// Para el cobro, el dto de payment ya existente (`SendPaymentAndPayFast`)
// acepta un campo opcional `idCuenta`: si lo trae, antes de marcar pagado
// el backend se encarga de absorber los Orders nuevos de la mesa hacia la
// sesión de la cuenta y luego cobra todo.

const initialState = {
  isLoading: false,
  cuentas: [],
  // Cuenta seleccionada en el `PaymentDialog` para añadir/cobrar.
  selectedCuenta: null,
  // Orders cargados de la cuenta seleccionada (read-only en el modal).
  selectedCuentaOrders: [],
  isLoadingOrders: false,
  error: null,
};

const slice = createSlice({
  name: "cuentas",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isLoadingOrders = false;
      state.error = action.payload;
    },

    getCuentasSuccess(state, action) {
      state.isLoading = false;
      state.cuentas = action.payload || [];
    },

    upsertCuenta(state, action) {
      const c = action.payload;
      if (!c) return;
      const idx = state.cuentas.findIndex((x) => x.id === c.id);
      if (idx === -1) state.cuentas = [...state.cuentas, c];
      else
        state.cuentas = state.cuentas.map((x, i) => (i === idx ? c : x));
      // Si la cuenta actualizada es la seleccionada, refresca su copia.
      if (state.selectedCuenta?.id === c.id) state.selectedCuenta = c;
    },

    removeCuenta(state, action) {
      const id = action.payload;
      state.cuentas = state.cuentas.filter((c) => c.id !== id);
      if (state.selectedCuenta?.id === id) {
        state.selectedCuenta = null;
        state.selectedCuentaOrders = [];
      }
    },

    setSelectedCuenta(state, action) {
      state.selectedCuenta = action.payload || null;
      // Limpiamos los orders cacheados — se cargan tras llamar a
      // GetCuentaOrders. Mantenerlos del cuenta anterior daría info
      // incorrecta en la UI.
      state.selectedCuentaOrders = [];
    },

    clearSelectedCuenta(state) {
      state.selectedCuenta = null;
      state.selectedCuentaOrders = [];
    },

    startLoadingOrders(state) {
      state.isLoadingOrders = true;
    },
    getCuentaOrdersSuccess(state, action) {
      state.isLoadingOrders = false;
      state.selectedCuentaOrders = action.payload || [];
    },
  },
});

export default slice.reducer;

export const {
  getCuentasSuccess,
  setSelectedCuenta,
  clearSelectedCuenta,
  upsertCuenta,
  removeCuenta,
} = slice.actions;

// ── Thunks ──────────────────────────────────────────────────────────────────

export function GetCuentas() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get(`cuentas`);
      const list = res?.data?.cuentas ?? res?.data ?? [];
      dispatch(slice.actions.getCuentasSuccess(list));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function CreateCuenta(payload, { onSuccess } = {}) {
  return async (dispatch) => {
    try {
      const res = await axios.post(
        `cuentas`,
        JSON.stringify(payload || {}),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        },
      );
      const cuenta = res?.data?.cuenta ?? res?.data;
      if (cuenta) {
        dispatch(slice.actions.upsertCuenta(cuenta));
        onSuccess?.(cuenta);
      }
      return cuenta;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function EditCuenta(id, payload, { onSuccess } = {}) {
  return async (dispatch) => {
    try {
      const res = await axios.put(
        `cuentas/${id}`,
        JSON.stringify(payload || {}),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        },
      );
      const cuenta = res?.data?.cuenta ?? res?.data;
      if (cuenta) {
        dispatch(slice.actions.upsertCuenta(cuenta));
        onSuccess?.(cuenta);
      }
      return cuenta;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function GetCuentaOrders(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingOrders());
    try {
      const res = await axios.get(`cuentas/${id}/orders`);
      const orders = res?.data?.orders ?? res?.data ?? [];
      dispatch(slice.actions.getCuentaOrdersSuccess(orders));
      return orders;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      return [];
    }
  };
}

// "Fusionar con sesión": al cargar una cuenta en una mesa, mueve los
// pedidos de la sesión de la cuenta a la sesión de la mesa y vincula la
// cuenta a esa sesión (cuenta.IdSession = mesaSessionId,
// mesaSession.IdCuenta = cuenta.ID). A partir de ahí, todos los flujos
// (parcial / total / print) operan sobre una única sesión que contiene
// los items de la cuenta + los de la mesa, sin necesidad de lógica
// multi-sesión en el front. Idempotente.
export function MergeCuentaIntoMesa(idCuenta, mesaSessionId, { onSuccess } = {}) {
  return async (dispatch) => {
    try {
      const res = await axios.post(
        `cuentas/${idCuenta}/merge-with/${mesaSessionId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        },
      );
      const cuenta = res?.data?.cuenta ?? res?.data;
      if (cuenta) {
        dispatch(slice.actions.upsertCuenta(cuenta));
        dispatch(slice.actions.setSelectedCuenta(cuenta));
        onSuccess?.(cuenta);
      }
      return cuenta;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// "Guardar cuenta": extrae los pedidos pendientes de la sesión de la
// mesa (post-merge) hacia una nueva sesión sin mesa. Inverso de
// MergeCuentaIntoMesa cuando el camarero quiere pausar el trabajo con
// la cuenta sin cobrar — los pedidos quedan listos para retomar después.
export function SaveCuentaFromSession(idCuenta, sessionId, { onSuccess } = {}) {
  return async (dispatch) => {
    try {
      const res = await axios.post(
        `cuentas/${idCuenta}/save-from/${sessionId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        },
      );
      const cuenta = res?.data?.cuenta ?? res?.data;
      if (cuenta) {
        dispatch(slice.actions.upsertCuenta(cuenta));
        onSuccess?.(cuenta);
      }
      return cuenta;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// "Desvincular cuenta de la sesión": rompe el vínculo bidireccional
// session.IdCuenta ↔ cuenta.IdSession SIN tocar los pedidos. Los items
// que estaban en la mesa por el merge previo siguen ahí — el camarero
// los cobrará como pedidos normales. Útil cuando se seleccionó una
// cuenta por error y se quiere quitar el vínculo. El backend emite
// SignalR para que todos los TPVs de esa mesa refresquen el sidebar.
export function DetachCuentaFromSession(idCuenta, sessionId, { onSuccess } = {}) {
  return async (dispatch) => {
    try {
      const res = await axios.post(
        `cuentas/${idCuenta}/detach-from/${sessionId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        },
      );
      const cuenta = res?.data?.cuenta ?? res?.data;
      if (cuenta) {
        dispatch(slice.actions.upsertCuenta(cuenta));
        onSuccess?.(cuenta);
      }
      return cuenta;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// "Añadir a cuenta": reasigna los Orders de la sesión de la mesa a la
// sesión de la cuenta. El backend devuelve la cuenta actualizada con el
// total nuevo. El front, tras esto, suele liberar la mesa (cleanMesa) y
// snackbar "Sumado a {nombre}".
export function AddSessionToCuenta(idCuenta, sessionId, { onSuccess } = {}) {
  return async (dispatch) => {
    try {
      const res = await axios.post(
        `cuentas/${idCuenta}/anyadir/${sessionId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        },
      );
      const cuenta = res?.data?.cuenta ?? res?.data;
      if (cuenta) {
        dispatch(slice.actions.upsertCuenta(cuenta));
        onSuccess?.(cuenta);
      }
      return cuenta;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
